import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Loading from '../loading';
import ModalErrorMessage from '../modalErrorMessage/modalErrorMessage';

// Modal compartilhado para assinar documentos de saúde (atestado, receituário) com o certificado A1.
export default function SignHealthDocumentModal({
  open,
  onClose,
  onSign,
  onSigned,
  title,
  summary,
  confirmationLabel,
  confirmationRequiredMessage,
}) {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [certificatePassword, setCertificatePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (open) {
      setCertificatePassword('');
      setShowPassword(false);
      setConfirmed(false);
      setShowErrorMessage(false);
      setErrorMessage('');
    }
  }, [open]);

  const close = () => {
    // The password must not stay in memory after the modal is closed.
    setCertificatePassword('');
    onClose();
  };

  const sign = () => {
    if (!certificatePassword) {
      setShowErrorMessage(true);
      setErrorMessage('Informe a senha do certificado digital.');
      return;
    }
    if (confirmationLabel && !confirmed) {
      setShowErrorMessage(true);
      setErrorMessage(confirmationRequiredMessage);
      return;
    }

    setLoading(true);
    onSign(certificatePassword, confirmationLabel ? confirmed : null)
      .then((returnFromApi) => {
        setLoading(false);
        setCertificatePassword('');
        onSigned(returnFromApi);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(String(returnFromApi.response?.data ?? 'Erro ao assinar o documento.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Loading isToHidden={isLoading === false} />
        {showErrorMessage && (
          <ModalErrorMessage message={errorMessage} setShow={setShowErrorMessage} />
        )}

        {summary && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {summary}
          </Typography>
        )}

        <Typography variant="body2" sx={{ mb: 2 }}>
          O documento será gerado em PDF com QR Code de validação e assinado com o certificado
          digital ICP-Brasil do profissional. Depois de assinado, ele não pode ser alterado.
        </Typography>

        <TextField
          label="Senha do certificado digital"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          autoFocus
          autoComplete="off"
          value={certificatePassword}
          onChange={(e) => setCertificatePassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') sign();
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'ocultar senha' : 'mostrar senha'}
                  size="small"
                  onClick={() => setShowPassword((value) => !value)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          A senha é usada somente nesta assinatura e não fica salva no sistema.
        </Typography>

        {confirmationLabel && (
          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                sx={{ pt: 0 }}
              />
            }
            label={<Typography variant="body2">{confirmationLabel}</Typography>}
          />
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          Entregue ao paciente o PDF assinado. Se imprimir, imprima o próprio PDF: o QR Code permite
          validar o documento no app VALIDAR do ITI.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancelar</Button>
        <Button variant="contained" onClick={sign}>
          Assinar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SignHealthDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSign: PropTypes.func.isRequired,
  onSigned: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  summary: PropTypes.string,
  confirmationLabel: PropTypes.string,
  confirmationRequiredMessage: PropTypes.string,
};

SignHealthDocumentModal.defaultProps = {
  summary: null,
  confirmationLabel: null,
  confirmationRequiredMessage: 'Confirme para continuar.',
};
